import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { DataTable } from '@/components/DataTable.jsx';
import { FormField, Input } from '@/components/ui/Input.jsx';
import { Select } from '@/components/ui/Select.jsx';
import { Badge } from '@/components/ui/Badge.jsx';
import { formatDateTime } from '@/lib/format.js';

const ACTION_VARIANT = {
  create: 'success', update: 'info', delete: 'danger',
  finalize: 'success', reopen: 'warning', mark_paid: 'brand',
  compute: 'info', recompute: 'info', login: 'default', logout: 'default',
};

export default function AuditLogPage() {
  const [filter, setFilter] = useState({ entity: '', action: '' });
  const params = new URLSearchParams();
  if (filter.entity) params.set('entity', filter.entity);
  if (filter.action) params.set('action', filter.action);
  const { data = [], isLoading } = useQuery({
    queryKey: ['audit', filter],
    queryFn: () => api.get(`/audit?${params.toString()}`),
  });

  const columns = [
    { key: 'created_at', header: 'When', sortable: true, render: (r) => formatDateTime(r.created_at) },
    { key: 'user_name', header: 'User', render: (r) => r.user_name || r.user_email || '—' },
    { key: 'entity', header: 'Entity', sortable: true },
    { key: 'entity_id', header: 'Entity ID', className: 'font-mono' },
    { key: 'action', header: 'Action', render: (r) => <Badge variant={ACTION_VARIANT[r.action] || 'default'}>{r.action}</Badge> },
    { key: 'before_json', header: 'Before', render: (r) => r.before_json ? <code className="text-xs text-slate-500">{r.before_json.length > 60 ? `${r.before_json.slice(0, 60)}…` : r.before_json}</code> : '—' },
    { key: 'after_json', header: 'After', render: (r) => r.after_json ? <code className="text-xs text-slate-500">{r.after_json.length > 60 ? `${r.after_json.slice(0, 60)}…` : r.after_json}</code> : '—' },
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Audit Log" description="Track all create/update/delete/decision actions" />
      <Section>
        <div className="mb-3 grid gap-3 md:grid-cols-3">
          <FormField label="Entity"><Input placeholder="e.g. employee, payroll_run" value={filter.entity} onChange={(e) => setFilter({ ...filter, entity: e.target.value })} /></FormField>
          <FormField label="Action">
            <Select value={filter.action} onChange={(e) => setFilter({ ...filter, action: e.target.value })}>
              <option value="">All</option>
              {Object.keys(ACTION_VARIANT).map((a) => <option key={a} value={a}>{a}</option>)}
            </Select>
          </FormField>
        </div>
        <DataTable columns={columns} data={data} loading={isLoading} pageSize={20} exportFilename="audit-log.csv" />
      </Section>
    </div>
  );
}
